import React, { useState, useEffect } from 'react'
import {
  useSettingsGeneral,
  useUpdateGeneral,
} from '@/v2/hooks/settings/useSettingsGeneral'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import TextField from '@/v2/components/common/TextField'
import SelectField from '@/v2/components/common/SelectField'
import InputAdornment from '@mui/material/InputAdornment'
import {
  SettingsPage,
  PrimaryButton,
  SecondaryButton,
  HeaderActions,
  Fieldset,
  FieldsetTitle,
  Row,
  SideNote,
  SideNoteText,
  CopyInputContainer,
  CopyInput,
  CopyButton,
  Form,
} from '@/v2/components/settings/shared'

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
]

const INITIAL_FORM = {
  name: '',
  phone_number: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zipcode: '',
  npi: '',
  tax_id: '',
  taxonomy_codes: [],
  google_analytics_id: '',
  facebook_pixel_id: '',
  facebook_conversion_token: '',
  tag_manager_key: '',
  processing_fee_percentage: '',
}

const mapClinicToForm = data => {
  const address = data.address || {}
  return {
    name: data.name || '',
    phone_number: data.phoneNumber || '',
    address1: address.address1 || '',
    address2: address.address2 || '',
    city: address.city || '',
    state: address.state || '',
    zipcode: address.zipcode || '',
    npi: data.npi || '',
    tax_id: data.taxId || '',
    taxonomy_codes: data.taxonomyCodes || [],
    google_analytics_id: data.googleAnalyticsId || '',
    facebook_pixel_id: data.facebookPixelId || '',
    facebook_conversion_token: data.facebookConversionToken || '',
    tag_manager_key: data.tagManagerKey || '',
    processing_fee_percentage:
      data.processingFeePercentage == null
        ? ''
        : String(data.processingFeePercentage),
  }
}

const MyClinic = () => {
  const { onOpenSnackbar } = useSnackbarContext()
  const { clinic: clinicData, isLoading: initialLoading } = useSettingsGeneral()
  const { updateGeneral, isPending: loading } = useUpdateGeneral()

  const [formData, setFormData] = useState(INITIAL_FORM)
  const [hasChanges, setHasChanges] = useState(false)

  const twilioPhoneNumber = clinicData?.twilioPhoneNumber || ''
  const addressId = clinicData?.address?.id || null

  const getClinicUrl = () => {
    const hostname = window.location.hostname
    const subdomain = hostname.split('.')[0]
    return subdomain !== 'localhost' && subdomain !== '127'
      ? `https://${hostname}`
      : `https://demo.clearworks.com`
  }

  const clinicUrl = getClinicUrl()

  useEffect(() => {
    if (clinicData) {
      setFormData(mapClinicToForm(clinicData))
    }
  }, [clinicData])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    try {
      const addressAttributes = {
        address1: formData.address1,
        address2: formData.address2,
        city: formData.city,
        state: formData.state,
        zipcode: formData.zipcode,
      }
      if (addressId) {
        addressAttributes.id = addressId
      }

      await updateGeneral({
        name: formData.name,
        phone_number: formData.phone_number,
        npi: formData.npi,
        tax_id: formData.tax_id,
        taxonomy_codes: formData.taxonomy_codes,
        google_analytics_id: formData.google_analytics_id,
        facebook_pixel_id: formData.facebook_pixel_id,
        facebook_conversion_token: formData.facebook_conversion_token,
        tag_manager_key: formData.tag_manager_key,
        processing_fee_percentage:
          formData.processing_fee_percentage === ''
            ? null
            : Number(formData.processing_fee_percentage),
        addresses_attributes: [addressAttributes],
      })

      setHasChanges(false)
      onOpenSnackbar('Clinic updated successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to update clinic', 'error')
    }
  }

  const handleCancel = () => {
    if (clinicData) {
      setFormData(mapClinicToForm(clinicData))
    }
    setHasChanges(false)
  }

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
    onOpenSnackbar('Copied to clipboard!', 'success')
  }

  return (
    <SettingsPage
      title="My clinic"
      loading={initialLoading}
      actions={
        <HeaderActions>
          {hasChanges && (
            <SecondaryButton onClick={handleCancel}>Cancel</SecondaryButton>
          )}
          <PrimaryButton
            onClick={handleSubmit}
            disabled={loading || !hasChanges}
          >
            {loading ? 'Saving...' : 'Save'}
          </PrimaryButton>
        </HeaderActions>
      }
    >
      <Form onSubmit={handleSubmit}>
        <Fieldset>
          <FieldsetTitle>Clinic information</FieldsetTitle>
          <Row $columns="7fr 5fr">
            <TextField
              fullWidth
              label="Clinic name"
              placeholder="E.g.: Pretty Smile"
              value={formData.name}
              onChange={val => handleInputChange('name', val)}
            />
            <TextField
              fullWidth
              label="Contact phone"
              placeholder="E.g.: (123) 456-7890"
              value={formData.phone_number}
              onChange={val => handleInputChange('phone_number', val)}
            />
          </Row>

          <Row $columns="2fr 5fr 5fr">
            <SelectField
              fullWidth
              label="Type"
              options={[{ value: 'primary', label: 'Primary' }]}
              value="primary"
              disabled
            />
            <TextField
              fullWidth
              label="Address"
              placeholder="E.g.: 12345 Main St"
              value={formData.address1}
              onChange={val => handleInputChange('address1', val)}
            />
            <TextField
              fullWidth
              label="Apartment/Suite"
              placeholder="E.g.: Apt 101"
              value={formData.address2}
              onChange={val => handleInputChange('address2', val)}
            />
          </Row>

          <Row $columns="5fr 4fr 3fr">
            <TextField
              fullWidth
              label="City"
              placeholder="E.g.: Boston"
              value={formData.city}
              onChange={val => handleInputChange('city', val)}
            />
            <SelectField
              fullWidth
              label="State"
              placeholder="Select State"
              options={US_STATES}
              value={formData.state}
              onChange={val => handleInputChange('state', val)}
            />
            <TextField
              fullWidth
              label="Zipcode"
              placeholder="E.g.: 12345"
              value={formData.zipcode}
              onChange={val => handleInputChange('zipcode', val)}
            />
          </Row>
        </Fieldset>

        <Fieldset>
          <FieldsetTitle>Clinic URL</FieldsetTitle>
          <SideNote>
            <div>
              <CopyInputContainer>
                <CopyInput type="text" value={clinicUrl} disabled />
                <CopyButton
                  type="button"
                  onClick={() => copyToClipboard(clinicUrl)}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                  Copy
                </CopyButton>
              </CopyInputContainer>
            </div>
            <SideNoteText style={{ paddingTop: '0.5rem' }}>
              Contact our support team if you want to update your URL.
            </SideNoteText>
          </SideNote>
        </Fieldset>

        {twilioPhoneNumber && (
          <Fieldset>
            <FieldsetTitle>Messages</FieldsetTitle>
            <SideNote>
              <div>
                <CopyInputContainer>
                  <CopyInput type="text" value={twilioPhoneNumber} disabled />
                  <CopyButton
                    type="button"
                    onClick={() => copyToClipboard(twilioPhoneNumber)}
                  >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy
                  </CopyButton>
                </CopyInputContainer>
              </div>
              <SideNoteText style={{ paddingTop: '0.5rem' }}>
                This is your business number. Patients will be able to contact
                you via sms and reply to reminders.
              </SideNoteText>
            </SideNote>
          </Fieldset>
        )}

        <Fieldset>
          <FieldsetTitle>Insurance</FieldsetTitle>
          <Row $columns="1fr 1fr">
            <TextField
              fullWidth
              label="NPI"
              placeholder="E.g.: 1234567890"
              value={formData.npi}
              onChange={val => handleInputChange('npi', val)}
            />
            <TextField
              fullWidth
              label="TaxID"
              placeholder="E.g.: 123456789"
              value={formData.tax_id}
              onChange={val => handleInputChange('tax_id', val)}
            />
          </Row>
          <Row>
            <TextField
              fullWidth
              label="Taxonomy Codes"
              placeholder="E.g.: 1223G0001X, 122300000X"
              value={(formData.taxonomy_codes || []).join(', ')}
              onChange={val =>
                handleInputChange(
                  'taxonomy_codes',
                  val
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean)
                )
              }
            />
          </Row>
        </Fieldset>

        <Fieldset>
          <FieldsetTitle>Payments</FieldsetTitle>
          <SideNote>
            <Row $columns="1fr 1fr">
              <TextField
                fullWidth
                label="Credit card processing fee"
                placeholder="E.g.: 2.9"
                type="number"
                inputProps={{ step: '0.01', min: 0, max: 100 }}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                value={formData.processing_fee_percentage}
                onChange={val =>
                  handleInputChange('processing_fee_percentage', val)
                }
              />
            </Row>
            <SideNoteText>
              Default percentage added to credit card payments to cover
              processing fees. Leave blank to charge no fee.
            </SideNoteText>
          </SideNote>
        </Fieldset>

        <Fieldset>
          <FieldsetTitle>Analytics</FieldsetTitle>

          <SideNote>
            <TextField
              fullWidth
              label="Google Measurement ID from Analytics"
              placeholder="E.g.: G-PSW1MY7HB4"
              value={formData.google_analytics_id}
              onChange={val => handleInputChange('google_analytics_id', val)}
            />
            <SideNoteText>
              This is optional. Provide the ID to enable Google Analytics in
              your application.
            </SideNoteText>
          </SideNote>

          <SideNote>
            <TextField
              fullWidth
              label="Facebook Pixel ID"
              placeholder="E.g.: 123456789012345"
              value={formData.facebook_pixel_id}
              onChange={val => handleInputChange('facebook_pixel_id', val)}
            />
            <SideNoteText>
              This is optional. Provide the ID to enable Facebook Pixel in your
              application.
            </SideNoteText>
          </SideNote>

          <SideNote>
            <TextField
              fullWidth
              label="Facebook Conversion Token"
              placeholder="E.g.: EAAEuZSxyZCDvEZCZ..."
              value={formData.facebook_conversion_token}
              onChange={val =>
                handleInputChange('facebook_conversion_token', val)
              }
            />
            <SideNoteText>
              This is optional. Provide the Token to enable Facebook Conversion
              Api on your application.
            </SideNoteText>
          </SideNote>

          <SideNote>
            <TextField
              fullWidth
              label="Tag Manager Key"
              placeholder="E.g.: GTM-UBPRZMC"
              value={formData.tag_manager_key}
              onChange={val => handleInputChange('tag_manager_key', val)}
            />
            <SideNoteText>
              This is optional. Provide the Key to enable Tag Manager Api on
              your application.
            </SideNoteText>
          </SideNote>
        </Fieldset>
      </Form>
    </SettingsPage>
  )
}

export default MyClinic
